/**
 * useReducedMotion Hook
 * Detects user's motion preference and provides consistent animation settings
 * Respects prefers-reduced-motion media query for accessibility
 */

import { useState, useEffect, useMemo } from 'react';

/**
 * Animation configuration based on motion preference
 */
interface MotionConfig {
  /** Whether user prefers reduced motion */
  prefersReducedMotion: boolean;
  /** Duration multiplier (0 for reduced motion, 1 for normal) */
  durationMultiplier: number;
  /** Standard transition duration in seconds */
  duration: number;
  /** Spring animation config for framer-motion */
  spring: {
    type: 'spring';
    stiffness: number;
    damping: number;
    duration: number;
  };
  /** Tween animation config for motion */
  tween: {
    type: 'tween';
    duration: number;
    ease: [number, number, number, number];
  };
}

/**
 * Hook to detect and respond to user's reduced motion preference
 * Provides consistent animation configurations across the app
 *
 * @returns Motion configuration object with animation settings
 *
 * @example
 * const { prefersReducedMotion, spring, tween } = useReducedMotion();
 *
 * <motion.div
 *   animate={{ opacity: 1 }}
 *   transition={spring}
 * />
 */
export function useReducedMotion(): MotionConfig {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent): void => {
      setPrefersReducedMotion(event.matches);
    };

    // Use addEventListener with fallback for older browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older Safari
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Memoize the config object to prevent unnecessary re-renders
  const config = useMemo<MotionConfig>(() => {
    const durationMultiplier = prefersReducedMotion ? 0 : 1;
    const baseDuration = 0.3;

    return {
      prefersReducedMotion,
      durationMultiplier,
      duration: baseDuration * durationMultiplier,
      spring: {
        type: 'spring',
        stiffness: prefersReducedMotion ? 500 : 400,
        damping: prefersReducedMotion ? 50 : 25,
        duration: prefersReducedMotion ? 0 : 0.3,
      },
      tween: {
        type: 'tween',
        duration: baseDuration * durationMultiplier,
        ease: [0.16, 1, 0.3, 1], // Custom ease curve
      },
    };
  }, [prefersReducedMotion]);

  return config;
}

export default useReducedMotion;
